import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  Button,
} from '@mui/material';
import {
  Mail as MailIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  where, 
  orderBy,
  updateDoc,
  doc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const Messages = () => {
  const { currentUser, userData } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Écouter les messages en temps réel
  useEffect(() => {
    if (!userData?.establishmentId || !currentUser?.uid) return;

    const messagesRef = collection(db, 'messages');
    
    // Requête pour les messages reçus
    const receivedQuery = query(
      messagesRef,
      where('establishmentId', '==', userData.establishmentId),
      where('recipientId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    // Requête pour les messages envoyés
    const sentQuery = query(
      messagesRef,
      where('establishmentId', '==', userData.establishmentId),
      where('senderId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeReceived = onSnapshot(receivedQuery, (receivedSnapshot) => {
      const receivedMessages = receivedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'received'
      }));
      
      setMessages(prevMessages => {
        const sentMessages = prevMessages.filter(msg => msg.type === 'sent');
        return [...sentMessages, ...receivedMessages].sort((a, b) => 
          b.createdAt?.toMillis() - a.createdAt?.toMillis()
        );
      });
      
      setLoading(false);
    }, (error) => {
      console.error('Erreur lors de la récupération des messages reçus:', error);
      setError(error.message);
      setLoading(false);
    });

    const unsubscribeSent = onSnapshot(sentQuery, (sentSnapshot) => {
      const sentMessages = sentSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        type: 'sent'
      }));
      
      setMessages(prevMessages => {
        const receivedMessages = prevMessages.filter(msg => msg.type === 'received');
        return [...receivedMessages, ...sentMessages].sort((a, b) => 
          b.createdAt?.toMillis() - a.createdAt?.toMillis()
        );
      });
      
      setLoading(false);
    }, (error) => {
      console.error('Erreur lors de la récupération des messages envoyés:', error);
      setError(error.message);
      setLoading(false);
    });

    return () => {
      unsubscribeReceived();
      unsubscribeSent();
    };
  }, [userData, currentUser]);

  // Marquer un message comme lu
  const markAsRead = async (messageId) => {
    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, {
        read: true
      });
    } catch (error) {
      console.error('Erreur lors du marquage du message comme lu:', error);
    }
  };

  // Gérer le clic sur un message
  const handleMessageClick = (messageId) => {
    const message = messages.find(m => m.id === messageId);
    if (message && !message.read) {
      markAsRead(messageId);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/personnel/dashboard')}
        sx={{ mb: 2 }}
        variant="outlined"
      >
        Retour au tableau de bord
      </Button>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <MailIcon sx={{ mr: 1 }} color="primary" />
          <Typography variant="h6">
            Messages
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : messages.length === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            Aucun message
          </Typography>
        ) : (
          <List>
            {messages.map((message) => (
              <React.Fragment key={message.id}>
                <ListItem
                  onClick={() => handleMessageClick(message.id)}
                  sx={{
                    backgroundColor: message.read ? 'inherit' : 'rgba(33, 150, 243, 0.08)',
                    borderRadius: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: message.read ? 'rgba(0, 0, 0, 0.04)' : 'rgba(33, 150, 243, 0.12)'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>
                      {message.senderRole === 'admin' ? (
                        <SupervisorIcon />
                      ) : (
                        <PersonIcon />
                      )}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography component="span" variant="subtitle1">
                          {message.senderName}
                        </Typography>
                        {!message.read && (
                          <Box
                            component="span"
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                              ml: 1
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{ display: 'block' }}
                        >
                          {message.content}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {format(message.createdAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: fr })}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default Messages;
